import icons from "@/assets/json/icons.json";
import { notifySuccess } from "@/toast-notifications";
import { sanitize } from "dompurify";
import { sendBlobToDownloadNativeWebViewMessage } from "./native";
import { IS_NATIVE_WEBVIEW } from "@/constants/native";

/**
 * Returns the first n multiples of a given number.
 * @param {number} input Number to multiply.
 * @param {number} n Length of the list.
 * @param {boolean} excludeZero If true, the list will start with the given multiple.
 * @returns {number[]}
 */
export const getNMultiples = (input, n, excludeZero = false) => [...Array(n).keys()].map(v => (v + excludeZero) * input);

/**
 * Returns the content of a css variable.
 * @param {string} variableName 
 * @returns {string}
 */
export const getCSSvar = (variableName) => getComputedStyle(document.documentElement).getPropertyValue(variableName);

/**
 * 
 * @param {(...any) => any} patient 
 * @param {'min'|'s'|'ms'} unit
 * @returns {unknown} 
 */
export const measureTime = (patient, unit = 'ms') => {
    const units = {
        min: 60000,
        s: 1000,
        ms: 1,
    };
    const start = performance.now();
    const patientResult = patient();
    const result = (performance.now() - start) / units[unit];
    console.log(`${result}${unit}`);
    return patientResult;
}

/**
 * 
 * @param {number} input 
 * @returns {string}
 */
export const toHexString = (input, isColor = false) => {
    return (isColor ? '#' : '') + input.toString(16).toUpperCase();
}

/**
 * 
 * @param {{[string]: any}} params 
 * @param {string} startWith 
 * @param {string} separator 
 * @returns {string}
 */
export const dictToParams = (params = {}, startWith = '?', separator = '&') => {
    const result = []
    for (const key in params) {
        result.push(`${key}=${params[key]}`);
    }
    return `${startWith}${result.join(separator)}`;
}

export const randomColor = () => `#${Math.floor(Math.random() * 16777215).toString(16)}`;
/**
 * Determine whether the given `input` is iterable.
 * @param {any} input
 * @returns {boolean}
 */
export function isIterable(input) {
    if (input === null || input === undefined) {
        return false
    }

    return typeof input[Symbol.iterator] === 'function';
}

/**
 * Array-ify a value.
 * @param {any} value 
 * @returns 
 */
export const toArray = (value) => {
    return isIterable(value) ? [...value] : [value];
}

/**
 * Set-ify a value.
 * @param {any} value 
 * @returns 
 */
export const toSet = (value) => {
    return new Set(isIterable(value) ? value : [value]);
}

/**
 * 
 * @param {string} name 
 * @returns 
 */
export const getIcon = (name) => {
    return icons[name] ?? "";
}

/**
 * 
 * @param {string} input 
 * @param {number} nChars 
 */
export const cutString = (input, nChars = 8, appendStr = "") => {
    const reg = new RegExp(`.{1,${nChars}}`);
    const match = (input.match(reg) ?? [])[0];
    return `${match ?? ''}${match === input ? '' : appendStr}`;
}

/**
 * Returns the result hex code of a color with a given opacity on top of another color (default white).   
 * @param {string} color hex value #RRGGBB
 * @param {number} opacity from `0` to `1`
 * @param {string} base hex value #RRGGBB (default: `#FFFFFF`)
 * @returns {string} hex value #RRGGBB
 */
export function opacityBlendColor(color, opacity, base = "#FFFFFF") {
    opacity = opacity > 1 ? 1 : opacity < 0 ? 0 : opacity;
    const regex = /^#(?<R>[a-f\d]{2})(?<G>[a-f\d]{2})(?<B>[a-f\d]{2})$/;
    const colorMatch = color.trim().toLowerCase().match(regex);
    const baseMatch = base.trim().toLowerCase().match(regex);
    if (!colorMatch || !baseMatch)
        return "#000000";
    const { R, G, B } = colorMatch.groups;
    const { R: bR, G: bG, B: bB } = baseMatch.groups;
    return [[R, bR], [G, bG], [B, bB]]
        .map(([color, baseColor]) => {
            color = parseInt(color, 16);
            baseColor = parseInt(baseColor, 16);
            color = Math.round((1 - opacity) * baseColor + opacity * color);
            return `${color < 0xF ? '0' : ''}${color.toString(16)}`;
        })
        .reduce((prev, curr) => `${prev}${curr}`, '#');
}

/**
 * Sorts a list of coordinate tuples clockwise.
 * @param {[number, number][]} points a list of `[x, y]` values
 * @returns {[number, number][]}
 */
export function sortPointsClockwise(points) {

    if (points.length == 0) return [];

    points = [...points];
    points.sort(([, y1], [, y2]) => y1 - y2);
    const cy = (points[0][1] + points[points.length - 1][1]) / 2;

    points.sort(([x1,], [x2,]) => x2 - x1);
    const cx = (points[0][0] + points[points.length - 1][0]) / 2;

    const angles = [];
    let startAngle = null;

    for (const [px, py] of points) {
        let angle = Math.atan2(py - cy, px - cx);
        if (!startAngle) {
            startAngle = angle;
        }
        else if (angle < startAngle) {
            angle += Math.PI * 2;
        }
        angles.push([px, py, angle]);
    }

    return angles
        .sort((a, b) => a[a.length - 1] - b[b.length - 1])
        .map(([x, y]) => [x, y]);
}

export const padNumber = (n = 0) => n.toString().padStart(2, '0');

/**
 * 
 * @param {any[]} haystack 
 * @param {any} needle 
 * @returns {number}
 */
export const searchForArray = (haystack, needle) => {
    let i, j, current;
    for (i = 0; i < haystack.length; ++i) {
        if (needle.length === haystack[i].length) {
            current = haystack[i];
            for (j = 0; j < needle.length && needle[j] === current[j]; ++j);
            if (j === needle.length)
                return i;
        }
    }
    return -1;
}

/**
 * 
 * @param {number[]} idsArray 
 * @returns {number[]}
 */
export const orderIds = (idsArray) => {
    return [...idsArray].sort();
}

/**
 * Save a blob as file.
 * @param {Blob} blob 
 * @param {string} filename 
 */
export async function saveBlobAsFile(blob, filename) {
    if (IS_NATIVE_WEBVIEW) {
        await sendBlobToDownloadNativeWebViewMessage(blob, filename);
    }
    else {
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    }
}

/**
 * Download content from a given url using default browser.
 * @param {string} url
 * @param {Record<string, unknown>} [options]
 */
export function downloadFromUrl(url, { target = "_blank", filename }) {
    // hehe funny workaround goes brrrrr
    const dummyAnchor = document.createElement('a');
    dummyAnchor.href = url;
    dummyAnchor.target = target;
    dummyAnchor.download = filename ?? "";
    document.body.appendChild(dummyAnchor);
    dummyAnchor.click();
    dummyAnchor.remove();
}

/**
 * Debounce function to execute code once per use case.
 * @see https://www.freecodecamp.org/news/javascript-debounce-example/
 * @param {Function} fn 
 * @param {number} timeout 
 * @return {Function}
 */
export function debounce(fn, timeout = 300) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), timeout);
    };
}

export function stopEventPropagation(e) {
    e.stopPropagation();
}

export function sortByPriorityCallback(o1, o2) {
    return o1.priority - o2.priority;
}

export function sortByIdCallback(o1, o2) {
    return o1.id - o2.id;
}

export function sortByNameCallback(o1, o2) {
    return o1.name - o2.name;
}

export function sortByPositionCallback(o1, o2) {
    return o1.position - o2.position;
}

/**
 * Compatibility layer for `toSorted` method in js arrays.
 * @template T
 * @param {T[]} array 
 * @param {(a: T, b: T) => number} [compareFn] 
 * @returns {T[]}
 */
export function toSorted(array, compareFn) {
    if ("toSorted" in array)
        return array.toSorted(compareFn);
    return [...array].sort(compareFn);
}

/**
 * @license
 * This code is licensed under the terms of the MIT license
 *
 * Copyright (c) 2022 Marco Antonio Anastacio Cintra <anastaciocintra@gmail.com>
 * and contributors: 
 * "@bayerlse" - Sebastian Bayerl
 * "@jrson83" -
 * "@bsor-dev"
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */

/**
 * Compare two data objects / arrays in javascript.
 *
 * @author Marco Antonio Anastacio Cintra
 * @link https://gist.github.com/anastaciocintra/6a9bf013d8bd940f857d5c9ad08990e1
 * @param {unknown} obj1 the first object.
 * @param {unknown} obj2 the second object.
 * @param {boolean} checkDataOrder determine if order  data is relevant on comparison
 *
 * @returns {boolean} return true if obj1 and obj2 have the same data
 *
 * @example
 *
 * expected results:
 * { a: 1, b: 2 } === { a: 1, b: 2 }
 * { a: 1, b: 2 } === { b: 2, a: 1 }
 * { a: 1, b: 3 } !== { b: 2, a: 1 }
 * [1, 2] === [1, 2]
 * [2, 1] === [1, 2]
 * [1, 3] !== [1, 2]
 *
 * but with checkDataOrder = true, the results are different for arrays:
 * [1, 2] === [1, 2] // same result as without checkDataOrder
 * [2, 1] !== [1, 2] // different order
 * [1, 3] !== [1, 2] // same result as without checkDataOrder
 *
 */
export const isEqual = (obj1, obj2, checkDataOrder = false) => {
    const checkDataOrderParanoic = false;
    if (obj1 === null || obj2 === null) {
        return obj1 === obj2;
    }
    let _obj1 = obj1;
    let _obj2 = obj2;
    if (!checkDataOrder) {
        if (obj1 instanceof Array) {
            _obj1 = [...obj1].sort();
        }
        if (obj2 instanceof Array) {
            _obj2 = [...obj2].sort();
        }
    }
    if (typeof _obj1 !== "object" || typeof _obj2 !== "object") {
        return _obj1 === _obj2;
    }

    const obj1Props = Object.getOwnPropertyNames(_obj1);
    const obj2Props = Object.getOwnPropertyNames(_obj2);
    if (obj1Props.length !== obj2Props.length) {
        return false;
    }

    if (checkDataOrderParanoic && checkDataOrder) {
        // whill result in {a:1, b:2} !== {b:2, a:1}
        // its not normal, but if you want this behavior, set checkDataOrderParanoic = true
        const propOrder = obj1Props.toString() === obj2Props.toString();
        if (!propOrder) {
            return false;
        }
    }

    for (const prop of obj1Props) {
        const val1 = _obj1[prop];
        const val2 = _obj2[prop];

        if (typeof val1 === "object" && typeof val2 === "object") {
            if (isEqual(val1, val2, checkDataOrder)) {
                continue;
            } else {
                return false;
            }
        }
        if (val1 !== val2) {
            return false;
        }
    }
    return true;
};

/**
 * Give a copy of a given object with filtered keys.
 * @param {Record<string, unknown>} object 
 * @param {(key: string, index: number, keys: string[]) => boolean} predicate
 * @returns {Record<string, unknown>}
 */
export function filterObjectKeys(object, predicate) {
    return Object.keys(object)
        .filter(predicate)
        .reduce((obj, key) => {
            obj[key] = object[key];
            return obj;
        }, {});
}

/**
 * Give a sanitized copy of a given object.
 * @param {Record<string, unknown>} object 
 * @param {(entry: [string, unknown], index: number, entries: [string, unknown][], object: Record<string, unknown>) => unknown} sanitizer
 * @returns {Record<string, unknown>}
 */
export function sanitizeObject(object, sanitizer) {
    return Object.entries(object)
        .map((entry, index, entries) => [entry[0], sanitizer(entry, index, entries, object)])
        .reduce((obj, [key, value]) => {
            obj[key] = value;
            return obj;
        }, {});
}

/**
 * Truncate text.
 * @param {string} text 
 * @param {number} maxLength 
 * @returns 
 */
export function truncateText(text, maxLength, replacement = '...') {
    const mustAddReplacement = text.length > maxLength;
    replacement = mustAddReplacement ? replacement : '';
    return text.substring(0, maxLength - replacement.length) + replacement;
}

/**
 * Deeply get a value from an object via its path.
 * @param {Record<string, unknown>} obj
 * @param {string | string[]} key
 * @param {any | undefined} def
 * @param {number} p
 */
export function getIn(
    obj,
    key,
    def = undefined,
    p = 0
) {
    // NOTE: naive way to parse path below
    const path = key instanceof Array ? key : key.split('.');
    while (obj && p < path.length) {
        obj = obj[path[p++]];
    }

    // check if path is not in the end
    if (p !== path.length && !obj) {
        return def;
    }

    return obj === undefined ? def : obj;
}

/**
 * Wait during some given time.
 * @param {number} ms 
 */
export async function wait(ms = 0) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Format number with a locale style.
 * @param {number} value 
 */
export function getNumberFormat(value, locale = "FR", style = "decimal") {
    return new Intl.NumberFormat(locale, { style }).format(value);
}

/**
 * Copy some text to the clipboard.
 * @param {string} text 
 * @param {string} [toastMessage]
 */
export async function copyToClipboard(text, toastMessage = null) {

    if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
    }
    else {
        console.warn("Clipboard object not found because you are in an unsecure context. Fallback to legacy method. More info at https://developer.mozilla.org/en-US/docs/Web/API/Navigator/clipboard.");

        const dummyTextArea = document.createElement("textarea");
        dummyTextArea.value = text;
        dummyTextArea.style.opacity = 0;
        dummyTextArea.style.position = "absolute";
        document.body.appendChild(dummyTextArea);
        dummyTextArea.focus();
        dummyTextArea.select();
        await new Promise((resolve, reject) => {
            try {
                // ooh scary deprecated method
                const success = document.execCommand('copy');
                success ? resolve() : reject();
            } catch (err) {
                reject(err);
            } finally {
                document.body.removeChild(dummyTextArea);
            }
        });
    }
    if (toastMessage)
        notifySuccess(toastMessage);
}
export function formatPrice(price, {
    locale = "fr",
    currency = "EUR",
    forceDecimal = false,
    showDashForZero = false,
} = {}) {
    const amount = price / 100;

    if (showDashForZero && amount === 0) {
        return "--";
    }
    const isInteger = Number.isInteger(amount);

    const options = {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
        minimumFractionDigits: forceDecimal ? 2 : (isInteger ? 0 : 2),
    };

    return amount.toLocaleString(locale, options);
}

export function formatPercent(value, {
    locale = "fr",
    forceDecimal = true,
    showDashForZero = false,
} = {}) {
    const percent = Number(value);

    if (showDashForZero && percent === 0) {
        return "--";
    }

    const options = {
        maximumFractionDigits: 2,
        minimumFractionDigits: forceDecimal ? 2 : 0,
    };

    return `${percent.toLocaleString(locale, options)} %`;
}


/**
 * @param {string} value 
 */
export function toSnakeCase(value) {
    return value.trim().toLowerCase().replace(/\W/g, "_");
}

/**
 * Use `sanitize` from `dompurify` under the hood to remove HTML tags from string.
 * @param {string} value 
 * @returns 
 */
export function removeHTMLTags(value) {
    return sanitize(value, { ALLOWED_TAGS: [], });
}

/**
 * Converts empty strings of an object to null values.
 * @param {Record<string, unknown>} o 
 */
export function emptyEntriesToNull(o) {
    const entries = Object.entries(o).map(([key, value]) => {
        const isString = typeof value === "string";
        return [
            key,
            (isString && value === "") ? null : value ?? null,
        ]
    });
    return Object.fromEntries(entries);
}

/**
 * Transforms a given blob into a base64 string.
 * @param {Blob} blob 
 * @returns {Promise<string>}
 */
export async function blobToBase64(blob) {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
    });
}

/**
 * Transforms a given a base64 string into a blob.
 * @param {string} value The base64 string value.
 * @returns 
 */
export async function base64ToBlob(value) {
    const result = await fetch(value);
    return result.blob();
}

/**
 * Shortcut function to build a `File` instance from a `Blob` instance.
 * @param {*} blob The blob instance.
 * @param {*} fileName The file instance name.
 * @returns 
 */
export function blobToFile(blob, fileName = "document") {
    return new File([blob], fileName);
}

/**
 * Check if a given value is a blob.
 * @param {unknown} value 
 * @returns {boolean}
 */
export function isBlob(value) {
    return value instanceof Blob;
}
export const capitalizeFirstLetter = (str) => {
    if (typeof str !== 'string' || str.length === 0) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Creates a `FormData` instance from an object.
 * @param {Record<string, unknown>} object
 */
export function objectToFormData(
    object,
    ignoreUndefined = true,
) {

    const formData = new FormData();

    for (const key in object) {
        if (!Object.hasOwn(object, key))
            continue;

        let element = object[key];

        const isElementObject = typeof element === "object";
        const isElementBlob = isBlob(element);
        const isElementNull = element === null;
        const isElementUndefined = element === undefined;

        if (isElementUndefined && ignoreUndefined)
            continue;

        if (isElementObject && !isElementBlob && !isElementNull) {
            element = JSON.stringify(element);
        }

        formData.set(key, element ?? '');
    }

    return formData;
}

export function isModernIpad() {
    return (
        /Macintosh/i.test(navigator.userAgent)
        && navigator.maxTouchPoints > 1
    );
}

/**
 * iPadOS 13+ reports a desktop-like userAgent ("Macintosh") instead of "iPad".
 * Libs rely on userAgent to detect mobile devices.
 * This patch restores the expected "iPad" string when touch is available on a Mac UA.
 */
export function patchIpadUserAgent() {

    if (!isModernIpad()) {
        return;
    }

    Object.defineProperty(navigator, "userAgent", {
        get: () => "iPad",
        configurable: true,
    });
}

export function isMobileDevice() {
    try {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    } catch (e) {
        return false;
    }
}

/**
 * Iterates over an array and returns the first non-null result
 * produced by applying the provided mapping function to each element.
 *
 * @template T, R
 * @param {Array<T>} array - The array of elements to iterate over.
 * @param {(element: T) => R | null} mapCallback - 
 *        A function that takes an element of type T and returns a value of type R or null.
 *        Iteration stops when a non-null value is returned.
 * @returns {R | undefined} - The first non-null result returned by the callback,
 *          or `undefined` if no such result is found.
 *
 * @example
 * const numbers = [1, 2, 3, 4];
 * const result = findMap(numbers, n => (n % 2 === 0 ? n * 10 : null));
 * console.log(result); // Output: 20
 */
export function findMap(array, finderCallback) {
    for (const element of array) {
        const elementResult = finderCallback(element);
        const isResultFound = elementResult !== null;
        if (isResultFound)
            return elementResult;
    }
}

/**
 * Checks whether a value is primitive (i.e. not a function or an object).
 *
 * Primitive types: string, number, boolean, bigint, symbol, undefined.
 * Non-primitive types: object (including null, arrays), function.
 *
 * @param {unknown} value - The value to check.
 * @returns {boolean} True if the value is primitive, false otherwise.
 *
 * @example
 * isValuePrimitive(42);         // true
 * isValuePrimitive("hello");    // true
 * isValuePrimitive({});         // false
 * isValuePrimitive(() => {});   // false
 */
export function isValuePrimitive(value) {
    const valueType = typeof value;
    return (
        valueType !== "function"
        && valueType !== "object"
    );
}


// WEBPACK FOOTER //
// ./src/helpers/utils.js